import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ThemeToggle from '@/components/ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Zap,
  LayoutDashboard,
  Battery,
  Clock,
  DollarSign,
  AlertTriangle,
  Settings,
  Moon,
  Sun,
  LogOut,
  ChevronLeft,
  Menu,
  X,
  Monitor,
  ClipboardList,
  Users,
} from 'lucide-react';

const SIDEBAR_KEY = 'vdrive-sidebar-collapsed';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'operations.view', end: true },
  { path: '/charger-requests', icon: ClipboardList, label: 'Charger Requests', permission: 'requests.view' },
  { path: '/chargers', icon: Battery, label: 'Chargers', permission: 'chargers.view' },
  { path: '/sessions', icon: Clock, label: 'Sessions', permission: 'operations.view' },
  { path: '/tariffs', icon: DollarSign, label: 'Tariffs', permission: 'operations.view' },
  { path: '/alarms', icon: AlertTriangle, label: 'Alarms', permission: 'operations.view' },
  { path: '/administration', icon: Users, label: 'Administration', permission: 'users.view' },
  { path: '/settings', icon: Settings, label: 'Settings', permission: '*' },
];

const formatRole = (role) => {
  if (!role) return 'Viewer';
  return role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const BrandMark = ({ compact = false }) => (
  <div className={cn('flex items-center gap-3 min-w-0', compact && 'justify-center')}>
    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
      <Zap className="h-5 w-5" aria-hidden="true" />
    </div>
    {!compact && (
      <div className="min-w-0">
        <p className="truncate font-heading text-lg font-bold leading-none">Vdrive</p>
        <p className="mt-1 truncate text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          CSMS
        </p>
      </div>
    )}
  </div>
);

const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const { resolvedTheme, theme, setTheme } = useTheme();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  ));

  const isDark = resolvedTheme === 'dark';
  const visibleNav = navItems.filter((item) => can(item.permission));
  const shellPad = collapsed ? '7rem' : '17.5rem';

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(collapsed));
    } catch {
      /* ignore private-mode storage failures */
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((part) => part[0]).join('').toUpperCase().slice(0, 2);
  };

  const isItemActive = (item) => (
    item.end
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );

  const renderNavLink = (item) => {
    const link = (
      <NavLink
        to={item.path}
        end={item.end}
        onClick={() => setMobileOpen(false)}
        title={collapsed && isDesktop ? item.label : undefined}
        aria-current={isItemActive(item) ? 'page' : undefined}
        className={cn(
          'flex min-h-11 items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isItemActive(item)
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-foreground hover:bg-foreground/10',
          collapsed && isDesktop && 'justify-center px-2'
        )}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className={cn(collapsed && isDesktop && 'sr-only')}>{item.label}</span>
      </NavLink>
    );

    if (!collapsed || !isDesktop) return link;

    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{ '--shell-pad': shellPad }}
      data-testid="app-layout"
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <header className="fixed top-0 left-0 right-0 z-50 px-3 pt-3 lg:left-[var(--shell-pad)] lg:px-5">
        <div className="glass flex h-14 items-center gap-2 px-2 sm:px-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full lg:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
            aria-controls="primary-navigation"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="min-w-0 lg:hidden">
            <BrandMark />
          </div>
          <p className="hidden min-w-0 truncate text-sm font-semibold lg:block">
            {visibleNav.find((item) => (
              item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
            ))?.label || 'Vdrive'}
          </p>
          <div className="ml-auto flex items-center gap-1">
            <ThemeToggle className="lg:hidden" testId="theme-toggle-mobile" />
            <ThemeToggle className="hidden lg:inline-flex" testId="theme-toggle" showLabel />
          </div>
        </div>
      </header>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-x-0 bottom-0 top-[4.5rem] z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        id="primary-navigation"
        className={cn(
          'glass-strong fixed z-40 flex flex-col transition-[transform,width] duration-300 max-lg:rounded-none',
          'left-0 top-[4.5rem] h-[calc(100%-4.5rem)] w-[min(100%,18rem)]',
          'lg:bottom-3 lg:left-3 lg:top-3 lg:h-auto',
          collapsed ? 'lg:w-[5.25rem]' : 'lg:w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        aria-label="Primary"
        aria-hidden={!isDesktop && !mobileOpen}
        inert={!isDesktop && !mobileOpen ? true : undefined}
        data-testid="sidebar"
      >
        <div className={cn(
          'hidden h-16 items-center gap-2 border-b border-white/30 px-3 dark:border-white/10 lg:flex',
          collapsed && isDesktop ? 'justify-center' : 'justify-between'
        )}>
          {collapsed && isDesktop ? (
            <Button
              variant="ghost"
              size="icon"
              className="hidden rounded-full lg:inline-flex"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              data-testid="expand-sidebar"
            >
              <Zap className="h-5 w-5" />
            </Button>
          ) : (
            <>
              <BrandMark />
              <Button
                variant="ghost"
                size="icon"
                className="hidden rounded-full lg:inline-flex"
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
                data-testid="collapse-sidebar"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </>
          )}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Sections">
          {visibleNav.map((item) => (
            <div key={item.path}>{renderNavLink(item)}</div>
          ))}
        </nav>

        <div className={cn('shrink-0 border-t border-white/30 p-3 dark:border-white/10', collapsed && isDesktop && 'px-2')}>
          <UserMenu
            user={user}
            initials={getInitials(user?.name)}
            isDark={isDark}
            theme={theme}
            setTheme={setTheme}
            onLogout={logout}
            collapsed={collapsed && isDesktop}
          />
        </div>
      </aside>

      <main
        id="main-content"
        className="min-h-screen px-3 pb-8 pt-[5.75rem] transition-[padding] duration-300 sm:px-5 lg:pl-[var(--shell-pad)] lg:pr-5"
      >
        {children}
      </main>
    </div>
  );
};

const UserMenu = ({
  user,
  initials,
  isDark,
  theme,
  setTheme,
  onLogout,
  collapsed = false,
  compact = false,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        className={cn(
          'h-auto rounded-2xl py-1.5',
          compact ? 'w-11 justify-center px-0 sm:w-auto sm:justify-start sm:gap-2 sm:px-2' : 'w-full justify-start gap-3 px-2',
          collapsed && 'w-11 justify-center px-0'
        )}
        aria-label="Account menu"
        data-testid={compact ? 'user-menu-header' : 'user-menu'}
      >
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-primary text-sm font-semibold text-primary-foreground">
            {initials}
          </AvatarFallback>
        </Avatar>
        {!collapsed && !compact && (
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-medium">{user?.name || 'User'}</span>
            <span className="block truncate text-xs text-muted-foreground">{formatRole(user?.role)}</span>
          </span>
        )}
        {compact && (
          <span className="hidden min-w-0 text-left sm:block">
            <span className="block max-w-[9rem] truncate text-sm font-medium">{user?.name || 'User'}</span>
          </span>
        )}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent side="right" align="end" sideOffset={10} className="w-60">
      <DropdownMenuLabel>
        <span className="block truncate">{user?.name || 'My account'}</span>
        <span className="block truncate text-xs font-normal text-muted-foreground">{formatRole(user?.role)}</span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        data-testid="theme-toggle-menu"
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        {isDark ? 'Light mode' : 'Dark mode'}
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => setTheme('system')}
        data-testid={compact ? 'theme-system-header' : 'theme-system'}
      >
        <Monitor className="h-4 w-4" />
        Use system theme
        {theme === 'system' && <span className="ml-auto text-xs text-muted-foreground">On</span>}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={onLogout}
        className="text-destructive focus:text-destructive"
        data-testid={compact ? 'logout-btn-header' : 'logout-btn'}
      >
        <LogOut className="h-4 w-4" />
        Log out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default Layout;
